import api, { buildURL, getPaginationTotal } from './api-service';
import UserService from '~/modules/services/user-service';

import _ from 'lodash';

export const SEARCH_USERS_QUERY = `
query searchUsers($q: String) {
  users(q: $q, limit: 20) {
    nodes {
      id
      name
      emailNormal
      accountType
    }
  }
}
`;

export const SEARCH_GROUPS_QUERY = `
query searchGroups($q: String) {
  groups(q: $q, limit: 20) {
    nodes {
      id
      name
    }
  }
}
`;

const PAGE_SIZE = 100;

const PermissionService = {
	getUsers: (params) => {
		return api.graphql.request(SEARCH_USERS_QUERY, params?.q ? params : {})
			.then((response) => response?.users?.nodes ?? []);
	},
	getGroups: (params) => {
		return api.graphql.request(SEARCH_GROUPS_QUERY, params?.q ? params : {})
			.then((response) => response?.groups?.nodes ?? []);
	},

	getUser: (id) => {
		return api.client.get(buildURL('/users', { id })).then((response) => response.data[0]);
	},

	getGroup: (id) => {
		return api.client.get(buildURL('/groups', { id })).then((response) => {
			return response.data[0];

		});
	},
	getUserPermissions: (user, page=0) => {
		let start = page * PAGE_SIZE;
		let end = start + ( PAGE_SIZE - 1 );

		return api.client.get(
			buildURL('/permissions', { user: user }),
			{ headers: { 'X-Range': `resources=${start}-${end}` } }
		).then((response) => {
			let total = getPaginationTotal(response.headers['x-content-range']);
			let currentPage = Math.floor(start / 100);
			let hasMoreResults = total > end;
			return { permissions: response.data, total, currentPage, hasMoreResults };
		});
	},
	getGroupsPermissions: (groups) => {
		if(!_.isEmpty(groups)) {
			return api.client.get(buildURL('/permissions', { groups: groups })).then((response) => response.data);
		}
	},
	getShare: (id) => {
		return api.client.get(buildURL('/shares', { id })).then((response) => response.data);
	},
	getPermissions: ({ users }) => {
		let params = {
			users
		};

		if( !users ) {
			delete params.users;
		}

		return api.client.get(buildURL('/permissions', params));
	},
	getLanguagesPermissions: ({ assets }) => {
		let params = {
			assets
		};

		if( !assets ) {
			delete params.assets;
		}

		return api.client.get(buildURL('/permissions/languages', params));
	},
	getShareGuestInvitePermission: () => {
		return api.client.get('/permissions/shareGuestInvite')
			.then((response) => response.data?.allowed ?? false);
	},

	hasIngestPermission: (session) => {
		if( UserService.isAdmin(session) ) {
			return true;
		}
		return session.permissions?.length
			? !!session.permissions.find((permission) => {
				return permission.access?.ingest;
			})
			: false;
	},

	hasReportsPermission: (session) => {
		if( UserService.isAdmin(session) ) {
			return true;
		}
		return session.permissions?.length
			? !!session.permissions.find((permission) => {
				return _.intersection(
					Object.keys(permission.access).filter((key) => permission.access[key]),
					['downloadReport', 'encodeReport', 'exceptionReport', 'feedbackReport', 'groupSpendingReport', 'workRequestReport', 'shareReport']
				).length > 0;
			})
			: false;
	},

	deletePermissions: (id) => {
		return api.client.delete(`/permissions/${id}`);
	},
	createPermission: (data) => {
		return api.client.post('/permissions', data);
	},
	updatePermission: (id, data) => {
		return api.client.patch(`/permissions/${id}`, data);
	},
	updateSharePermission: (id, data) => {
		return api.client.patch(`/shares/${id}`, data);
	},
	transformPermission: (permission, isEditing) => {
		let transformed = {
			...permission,
			sources: {
				...permission.sources,
				adminWorkRequests: {
					...permission.sources?.adminWorkRequests,
					groups: permission.sources?.adminWorkRequests?.groups?.map((group) => group.id || group._id) || [],
					users: permission.sources?.adminWorkRequests?.users?.map((user) => user.id || user._id) || []
				},
				manageWorkRequests: {
					...permission.sources?.manageWorkRequests,
					groups: permission.sources?.manageWorkRequests?.groups?.map((group) => group.id || group._id) || [],
					users: permission.sources?.manageWorkRequests?.users?.map((user) => user.id || user._id) || []
				},
				shareReport: {
					...permission.sources?.shareReport,
					groups: permission.sources?.shareReport?.groups?.map((group) => group.id || group._id) || []
				}
			}
		};

		if( typeof transformed.project === 'boolean' ) {
			delete transformed.project;
		}
		else {
			transformed.project = permission.project?.value ?? permission.project;
		}

		if( typeof transformed.studio === 'boolean' ) {
			delete transformed.studio;
		}
		else {
			transformed.studio = permission.studio?.value ?? permission.studio;
		}

		if( typeof transformed.types === 'boolean' ) {
			transformed.types = [];
		}

		if( !transformed.assets || typeof transformed.assets === 'boolean' ) {
			transformed.assets = [];
		}
		else {
			transformed.assets = transformed.assets.map((asset) => typeof asset === 'object'
				? asset._id ?? asset.value
				: asset);
		}

		if( isEditing ) {
			delete transformed.user;
			delete transformed.approved;
			delete transformed._id;
		}

		// fields used for FE display
		delete transformed.users;
		delete transformed.groups;
		delete transformed.sources?.preview?.edit;
		delete transformed.sources?.requests?.edit;
		delete transformed.sources?.autosubs?.edit;
		delete transformed.sources?.download?.edit;
		delete transformed.$links;
		delete transformed.created;

		if( transformed.permissionType === 'assets' ) {
			transformed.types = [];
		}
		else {
			transformed.assets = [];
		}
		delete transformed.permissionType;

		return transformed;
	}
};

export default PermissionService;
